/*
 * Korean Hub – predictive search
 * ------------------------------------------------------------------
 * Replaces the theme's old Vue dropdown (dT_main.js), which:
 *   - listened to `keyup` + Vue v-model, so on Android keyboards (Gboard,
 *     Samsung…) nothing was searched until the word was finished, and
 *     pasted / autofilled text was never searched at all;
 *   - had no protection against slow responses arriving out of order, so
 *     results for an older, shorter query could overwrite the right ones;
 *   - only showed 6 products and had no "View all results" link;
 *   - only worked on the first search box on the page.
 *
 * This file:
 *   - listens to the `input` event (typing, paste, autofill, IME, scanners);
 *   - searches title, brand, type, tags, variant title, SKU and barcode;
 *   - converts Arabic-Indic digits (٠-٩ / ۰-۹) so SKUs/barcodes typed on an
 *     Arabic keyboard still match;
 *   - ignores stale responses, debounces, and caches queries;
 *   - puts products whose NAME contains the typed words first;
 *   - shows up to 10 products + "View all results", or a "no results" line;
 *   - supports ↑ ↓ Enter Esc.
 *
 * Markup contract (see snippets/dT_top-search.liquid and search-bar.liquid):
 *   <div data-kh-search> <form><input name="q"></form>
 *     <div data-kh-search-results></div> </div>
 * The results page fallback uses: <div data-kh-search-fallback data-q="…">
 */
(function () {
  'use strict';

  // Tells dT_main.js not to mount the old Vue predictive search.
  window.KHSearchEnabled = true;

  var CFG = window.KH_SEARCH || {};
  var STR = CFG.strings || {};
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var LIMIT = 10; // Shopify's maximum for predictive search
  var DEBOUNCE_MS = 150;
  var FIELDS = 'title,vendor,product_type,variants.title,variants.sku,variants.barcode,tag';

  /* ---------------- helpers ---------------- */

  function t(key, fallback) {
    return STR[key] || fallback;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ٠١٢٣٤٥٦٧٨٩ (Arabic) and ۰۱۲۳۴۵۶۷۸۹ (Persian) -> 0-9
  function latinDigits(s) {
    return String(s || '')
      .replace(/[٠-٩]/g, function (c) { return String(c.charCodeAt(0) - 0x0660); })
      .replace(/[۰-۹]/g, function (c) { return String(c.charCodeAt(0) - 0x06F0); });
  }

  function cleanQuery(q) {
    return latinDigits(q).replace(/\s+/g, ' ').trim();
  }

  // Lower-case, strip punctuation, unify Arabic letter forms – used only for ranking.
  function norm(s) {
    return latinDigits(s)
      .toLowerCase()
      .replace(/[ً-ٰٟـ]/g, '')
      .replace(/[أإآٱ]/g, 'ا')
      .replace(/ة/g, 'ه')
      .replace(/ى/g, 'ي')
      .replace(/[^0-9a-z؀-ۿ]+/g, ' ')
      .trim();
  }

  function searchUrl(q) {
    return ROOT + 'search?type=product&options%5Bprefix%5D=last&q=' + encodeURIComponent(cleanQuery(q));
  }

  function formatMoney(amount) {
    var num = parseFloat(amount);
    if (isNaN(num)) return '';
    var format = CFG.moneyFormat || '{{amount}}';
    var fixed2 = num.toFixed(2);
    var withCommas = function (n, dec) {
      var parts = n.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return dec === false ? parts[0] : parts.join('.');
    };
    var out = format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, withCommas(num.toFixed(0), false).replace(/,/g, '.'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withCommas(fixed2).replace(/[.,]/g, function (c) { return c === '.' ? ',' : '.'; }))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas(num.toFixed(0), false))
      .replace(/\{\{\s*amount\s*\}\}/, withCommas(fixed2));
    // money formats sometimes contain HTML – keep only the text
    var tmp = document.createElement('div');
    tmp.innerHTML = out;
    return tmp.textContent || tmp.innerText || out;
  }

  function sizedImage(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  /* ---------------- data ---------------- */

  var cache = {};

  function fetchProducts(q) {
    if (cache[q]) return cache[q];
    var params =
      'q=' + encodeURIComponent(q) +
      '&resources%5Btype%5D=product' +
      '&resources%5Blimit%5D=' + LIMIT +
      '&resources%5Boptions%5D%5Bunavailable_products%5D=last' +
      '&resources%5Boptions%5D%5Bfields%5D=' + encodeURIComponent(FIELDS);
    var p = fetch(ROOT + 'search/suggest.json?' + params, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (j) {
        return (j && j.resources && j.resources.results && j.resources.results.products) || [];
      })
      .catch(function (err) {
        delete cache[q];
        throw err;
      });
    cache[q] = p;
    return p;
  }

  // Name first: products whose title contains every typed word come first,
  // then titles containing some of the words; Shopify's order breaks ties.
  function rankByName(products, q) {
    var words = norm(q).split(' ').filter(Boolean);
    if (!words.length) return products;
    var phrase = words.join(' ');
    return products
      .map(function (p, i) {
        var title = norm(p.title);
        var squeezed = title.replace(/ /g, '');
        var hits = 0;
        words.forEach(function (w) {
          if ((' ' + title).indexOf(' ' + w) > -1 || squeezed.indexOf(w) > -1) hits++;
        });
        var score = hits / words.length;
        if (title.indexOf(phrase) > -1) score += 1;
        return { p: p, i: i, score: score };
      })
      .sort(function (a, b) { return b.score - a.score || a.i - b.i; })
      .map(function (x) { return x.p; });
  }

  // Returns { products, approximate }. If nothing matches a multi-word query,
  // retry once without the last word so the customer still sees something close.
  function search(q) {
    q = cleanQuery(q);
    return fetchProducts(q).then(function (products) {
      if (products.length) return { products: rankByName(products, q), approximate: false };
      var words = q.split(' ');
      if (words.length < 2) return { products: [], approximate: false };
      var shorter = words.slice(0, -1).join(' ');
      return fetchProducts(shorter).then(function (more) {
        return { products: rankByName(more, shorter), approximate: more.length > 0 };
      });
    });
  }

  /* ---------------- rendering ---------------- */

  function highlight(title, q) {
    var safe = escapeHtml(title);
    var words = cleanQuery(q).split(' ').filter(function (w) { return w.length > 1; });
    if (!words.length) return safe;
    var re = new RegExp('(' + words.map(function (w) {
      return escapeHtml(w).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }).join('|') + ')', 'gi');
    return safe.replace(re, '<mark>$1</mark>');
  }

  function priceHtml(p) {
    var price = parseFloat(p.price);
    var compare = parseFloat(p.compare_at_price_max);
    var html = '<span class="kh-ps__price">' + escapeHtml(formatMoney(p.price)) + '</span>';
    if (!isNaN(compare) && compare > price) {
      html += ' <s class="kh-ps__compare">' + escapeHtml(formatMoney(p.compare_at_price_max)) + '</s>';
    }
    if (p.available === false) {
      html += ' <span class="kh-ps__soldout">' + escapeHtml(t('soldOut', 'Sold out')) + '</span>';
    }
    return html;
  }

  function itemHtml(p, q, idx, uid) {
    var img = p.image || (p.featured_image && p.featured_image.url) || '';
    return (
      '<li class="autocomplete-results kh-ps__item" role="option" id="' + uid + '-opt-' + idx + '" aria-selected="false">' +
        '<a class="kh-ps__thumb" href="' + escapeHtml(p.url) + '" tabindex="-1" aria-hidden="true">' +
          (img ? '<img src="' + escapeHtml(sizedImage(img, 120)) + '" alt="" loading="lazy" width="60" height="60">' : '') +
        '</a>' +
        '<div class="media-body">' +
          '<a class="kh-ps__title" href="' + escapeHtml(p.url) + '">' + highlight(p.title, q) + '</a>' +
          '<div class="price">' + priceHtml(p) + '</div>' +
        '</div>' +
      '</li>'
    );
  }

  /* ---------------- predictive search box ---------------- */

  var uidCounter = 0;

  function PredictiveSearch(root) {
    this.root = root;
    this.form = root.querySelector('form');
    this.input = root.querySelector('input[name="q"]');
    this.box = root.querySelector('[data-kh-search-results]');
    if (!this.form || !this.input || !this.box) return;

    this.uid = 'kh-ps-' + (++uidCounter);
    this.requestId = 0;
    this.timer = null;
    this.active = -1;
    this.lastQuery = '';

    this.box.id = this.uid;
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-controls', this.uid);
    this.input.setAttribute('aria-expanded', 'false');

    var self = this;
    // `input` fires for typing, paste, autofill, voice, Android IME and barcode scanners.
    this.input.addEventListener('input', function () { self.schedule(); });
    this.input.addEventListener('focus', function () {
      if (self.input.value.trim() && self.box.innerHTML) self.open();
      else if (self.input.value.trim()) self.schedule();
    });
    this.input.addEventListener('keydown', function (e) { self.onKeydown(e); });
    this.form.addEventListener('submit', function () {
      // normalise Arabic digits so SKU / barcode searches work on the results page too
      self.input.value = cleanQuery(self.input.value);
    });
    document.addEventListener('click', function (e) {
      if (!self.root.contains(e.target)) self.close();
    });

    var clearBtn = root.querySelector('.dT_SearchClose');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        self.input.value = '';
        self.box.innerHTML = '';
        self.close();
        self.input.focus();
      });
    }
  }

  PredictiveSearch.prototype.schedule = function () {
    var self = this;
    clearTimeout(this.timer);
    var q = cleanQuery(this.input.value);
    var clearBtn = this.root.querySelector('.dT_SearchClose');
    if (clearBtn) clearBtn.style.display = q ? 'block' : 'none';
    if (!q) {
      this.requestId++; // cancel anything in flight
      this.box.innerHTML = '';
      this.close();
      return;
    }
    this.timer = setTimeout(function () { self.run(q); }, DEBOUNCE_MS);
  };

  PredictiveSearch.prototype.run = function (q) {
    var self = this;
    var id = ++this.requestId;
    this.root.classList.add('kh-ps--loading');
    search(q)
      .then(function (res) {
        if (id !== self.requestId) return; // a newer query was typed – ignore this answer
        self.render(q, res);
      })
      .catch(function () {
        if (id !== self.requestId) return;
        self.box.innerHTML = '';
        self.close();
      })
      .then(function () {
        if (id === self.requestId) self.root.classList.remove('kh-ps--loading');
      });
  };

  PredictiveSearch.prototype.render = function (q, res) {
    var self = this;
    this.lastQuery = q;
    this.active = -1;
    var html = '<div class="kh-ps" role="presentation">';
    if (res.products.length) {
      if (res.approximate) {
        html += '<p class="kh-ps__note">' + escapeHtml(t('closest', 'No exact match – closest products:')) + '</p>';
      }
      html += '<ul class="dt-sc-ProductSugesstions kh-ps__list" role="listbox">';
      res.products.forEach(function (p, i) { html += itemHtml(p, q, i, self.uid); });
      html += '</ul>';
      html += '<a class="kh-ps__all" href="' + escapeHtml(searchUrl(q)) + '">' +
        escapeHtml(t('viewAll', 'View all results')) + ' “' + escapeHtml(q) + '” →</a>';
    } else {
      html += '<p class="kh-ps__empty">' + escapeHtml(t('noResults', 'No products found for')) + ' “' + escapeHtml(q) + '”</p>';
    }
    html += '</div>';
    this.box.innerHTML = html;
    this.open();
  };

  PredictiveSearch.prototype.open = function () {
    this.box.style.display = 'block';
    this.root.classList.add('kh-ps--open');
    this.input.setAttribute('aria-expanded', 'true');
  };

  PredictiveSearch.prototype.close = function () {
    this.box.style.display = 'none';
    this.root.classList.remove('kh-ps--open');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.active = -1;
  };

  PredictiveSearch.prototype.items = function () {
    return Array.prototype.slice.call(this.box.querySelectorAll('.kh-ps__item'));
  };

  PredictiveSearch.prototype.setActive = function (idx) {
    var items = this.items();
    if (!items.length) return;
    if (idx < 0) idx = items.length - 1;
    if (idx >= items.length) idx = 0;
    items.forEach(function (li, i) {
      var on = i === idx;
      li.classList.toggle('is-active', on);
      li.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    this.active = idx;
    this.input.setAttribute('aria-activedescendant', items[idx].id);
    items[idx].scrollIntoView({ block: 'nearest' });
  };

  PredictiveSearch.prototype.onKeydown = function (e) {
    var key = e.key;
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      if (!this.items().length) return;
      e.preventDefault();
      this.open();
      this.setActive(this.active + (key === 'ArrowDown' ? 1 : -1));
    } else if (key === 'Enter') {
      var items = this.items();
      if (this.active > -1 && items[this.active] && this.root.classList.contains('kh-ps--open')) {
        e.preventDefault();
        window.location.href = items[this.active].querySelector('a.kh-ps__title').href;
      }
    } else if (key === 'Escape') {
      this.close();
    }
  };

  /* ---------------- results page: zero-result fallback ---------------- */

  function initFallback(el) {
    var q = el.getAttribute('data-q');
    if (!q) return;
    search(q).then(function (res) {
      if (!res.products.length) return;
      var html = '<h2 class="kh-fallback__title">' + escapeHtml(t('fallbackTitle', 'Products you might be looking for')) + '</h2>' +
        '<ul class="kh-fallback__grid">';
      res.products.forEach(function (p) {
        var img = p.image || (p.featured_image && p.featured_image.url) || '';
        html += '<li class="kh-fallback__card"><a href="' + escapeHtml(p.url) + '">' +
          (img ? '<img src="' + escapeHtml(sizedImage(img, 400)) + '" alt="" loading="lazy">' : '') +
          '<span class="kh-fallback__name">' + escapeHtml(p.title) + '</span>' +
          '<span class="kh-fallback__price">' + priceHtml(p) + '</span></a></li>';
      });
      html += '</ul>';
      el.innerHTML = html;
    }).catch(function () {});
  }

  /* ---------------- boot ---------------- */

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-kh-search]'), function (el) {
      if (el.__khSearch) return;
      el.__khSearch = new PredictiveSearch(el);
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-kh-search-fallback]'), initFallback);

    // Focus the field when the header search panel is opened.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.dT_TopStickySearchBtn');
      if (!btn) return;
      setTimeout(function () {
        var input = document.querySelector('#dT_top-sticky input[name="q"]');
        if (input) input.focus();
      }, 350);
    });
  }

  window.KHSearch = { search: search, searchUrl: searchUrl, cleanQuery: cleanQuery };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();